import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

export interface LeadGenForm {
  id: number;
  name: string;
  publicKey: string;
  description?: string;
  fieldsJson?: string;
  formType?: string;
  published: boolean;
  active: boolean;
}

export interface LeadGenGradeConfig {
  id: number;
  gradeCode: string;
  minScore: number;
  maxScore?: number;
  sortOrder: number;
  active: boolean;
}

export interface LeadGenCampaign {
  id: number;
  name: string;
  description?: string;
  budget: number;
  cost: number;
  channel?: string;
  targetAudience?: string;
  goals?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  revenueAttribution: number;
  active: boolean;
}

export interface LeadGenLandingPage {
  id: number;
  title: string;
  slug: string;
  contentHtml?: string;
  templateId?: string;
  customDomain?: string;
  abTestVariant?: string;
  version: number;
  published: boolean;
  viewsCount: number;
  conversionsCount: number;
}

export interface LeadGenScoringRule {
  id: number;
  ruleName: string;
  eventType: string;
  scoreDelta: number;
  conditionJson?: string;
  active: boolean;
}

export interface LeadGenRoutingRule {
  id: number;
  ruleName: string;
  priorityOrder: number;
  conditionJson: string;
  assignEmployeeId?: number;
  assignTeamCode?: string;
  territoryId?: number;
  active: boolean;
}

export interface LeadGenAbExperiment {
  id: number;
  experimentName: string;
  slug: string;
  variantPageIds: string;
  trafficWeights: string;
  statusCode: string;
  winnerPageId?: number;
}

export interface LeadGenEnrichment {
  id: number;
  leadId: number;
  companyName?: string;
  industry?: string;
  website?: string;
  employeeCount?: number;
  revenue?: number;
  country?: string;
  state?: string;
  city?: string;
  enrichmentSource?: string;
}

export interface LeadGenVisitorProfile {
  id: number;
  visitorCookieId: string;
  ipAddress?: string;
  email?: string;
  phone?: string;
  name?: string;
  leadId?: number;
}

export interface LeadGenConsentRecord {
  id: number;
  email: string;
  consentVersion: string;
  cookieConsent: boolean;
  privacyAcceptance: boolean;
  createdAt?: string;
}

export interface LeadGenSource {
  id: number;
  code: string;
  name: string;
  active: boolean;
}

export interface LeadGenDashboardResponse {
  totalVisitors: number;
  totalLeads: number;
  conversions: number;
  conversionRate: number;
  avgLeadScore: number;
  formSubmissions: number;
  activeCampaigns: number;
  publishedLandingPages: number;
  funnel: { stage: string; count: number; percent: string }[];
  topSources: { source: string; leads: number; conversionRate: string; roi: string }[];
  scoreDistribution: { grade: string; count: number }[];
  campaignPerformance: { id: number; name: string; budget: number; cost: number; revenueAttribution: number; roiPercentage: number }[];
}

export interface LeadGenReportsBundle {
  leadSourceReport: Record<string, unknown>[];
  campaignReport: Record<string, unknown>[];
  conversionReport: Record<string, unknown>[];
  scoringReport: Record<string, unknown>[];
  attributionReport: Record<string, unknown>[];
  roiReport: Record<string, unknown>[];
}

export const leadgenApi = {
  listForms: () =>
    unwrap(api.get<ApiResponse<LeadGenForm[]>>('/v1/lead-gen/forms')),

  createForm: (body: Partial<LeadGenForm>) =>
    unwrap(api.post<ApiResponse<LeadGenForm>>('/v1/lead-gen/forms', body)),

  updateForm: (id: number, body: Partial<LeadGenForm>) =>
    unwrap(api.put<ApiResponse<LeadGenForm>>(`/v1/lead-gen/forms/${id}`, body)),

  deleteForm: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/forms/${id}`)),

  getPublicForm: (publicKey: string) =>
    unwrap(api.get<ApiResponse<LeadGenForm>>(`/v1/lead-gen/public/forms/${publicKey}`)),

  trackVisitor: (body: { formPublicKey: string; cookieId: string; email?: string; phone?: string; name?: string }) =>
    unwrap(api.post<ApiResponse<LeadGenVisitorProfile>>('/v1/lead-gen/public/visitor/track', body)),

  submitPublic: (body: {
    formPublicKey: string;
    fields?: Record<string, unknown>;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    consentGiven: boolean;
    visitorProfileId?: number;
    visitorCookieId?: string;
  }) =>
    unwrap(api.post<ApiResponse<{ submissionId: number }>>('/v1/lead-gen/submissions', body)),

  listCampaigns: () => unwrap(api.get<ApiResponse<LeadGenCampaign[]>>('/v1/lead-gen/campaigns')),
  createCampaign: (body: Partial<LeadGenCampaign>) => unwrap(api.post<ApiResponse<LeadGenCampaign>>('/v1/lead-gen/campaigns', body)),
  updateCampaign: (id: number, body: Partial<LeadGenCampaign>) => unwrap(api.put<ApiResponse<LeadGenCampaign>>(`/v1/lead-gen/campaigns/${id}`, body)),
  deleteCampaign: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/campaigns/${id}`)),
  getCampaignRoi: (id: number) => unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/lead-gen/campaigns/${id}/roi`)),

  listLandingPages: () => unwrap(api.get<ApiResponse<LeadGenLandingPage[]>>('/v1/lead-gen/landing-pages')),
  createLandingPage: (body: Partial<LeadGenLandingPage>) => unwrap(api.post<ApiResponse<LeadGenLandingPage>>('/v1/lead-gen/landing-pages', body)),
  updateLandingPage: (id: number, body: Partial<LeadGenLandingPage>) => unwrap(api.put<ApiResponse<LeadGenLandingPage>>(`/v1/lead-gen/landing-pages/${id}`, body)),
  deleteLandingPage: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/landing-pages/${id}`)),

  listScoringRules: () => unwrap(api.get<ApiResponse<LeadGenScoringRule[]>>('/v1/lead-gen/scoring-rules')),
  createScoringRule: (body: Partial<LeadGenScoringRule>) => unwrap(api.post<ApiResponse<LeadGenScoringRule>>('/v1/lead-gen/scoring-rules', body)),
  updateScoringRule: (id: number, body: Partial<LeadGenScoringRule>) => unwrap(api.put<ApiResponse<LeadGenScoringRule>>(`/v1/lead-gen/scoring-rules/${id}`, body)),
  deleteScoringRule: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/scoring-rules/${id}`)),

  listRoutingRules: () => unwrap(api.get<ApiResponse<LeadGenRoutingRule[]>>('/v1/lead-gen/routing-rules')),
  createRoutingRule: (body: Partial<LeadGenRoutingRule>) => unwrap(api.post<ApiResponse<LeadGenRoutingRule>>('/v1/lead-gen/routing-rules', body)),
  updateRoutingRule: (id: number, body: Partial<LeadGenRoutingRule>) => unwrap(api.put<ApiResponse<LeadGenRoutingRule>>(`/v1/lead-gen/routing-rules/${id}`, body)),
  deleteRoutingRule: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/routing-rules/${id}`)),

  listVisitorProfiles: () => unwrap(api.get<ApiResponse<LeadGenVisitorProfile[]>>('/v1/lead-gen/visitor/profiles')),
  listConsentRecords: () => unwrap(api.get<ApiResponse<LeadGenConsentRecord[]>>('/v1/lead-gen/consent')),
  saveConsent: (body: Partial<LeadGenConsentRecord>) => unwrap(api.post<ApiResponse<LeadGenConsentRecord>>('/v1/lead-gen/consent', body)),

  getDashboard: () => unwrap(api.get<ApiResponse<LeadGenDashboardResponse>>('/v1/lead-gen/dashboard/summary')),
  getReports: () => unwrap(api.get<ApiResponse<LeadGenReportsBundle>>('/v1/lead-gen/reports')),

  listSources: () => unwrap(api.get<ApiResponse<LeadGenSource[]>>('/v1/lead-gen/sources')),
  createSource: (body: Partial<LeadGenSource>) => unwrap(api.post<ApiResponse<LeadGenSource>>('/v1/lead-gen/sources', body)),

  getFormEmbed: (id: number) => unwrap(api.get<ApiResponse<{ publicKey: string; embedCodes: Record<string, string> }>>(`/v1/lead-gen/forms/${id}/embed`)),

  getGradeConfig: () => unwrap(api.get<ApiResponse<LeadGenGradeConfig[]>>('/v1/lead-gen/grade-config')),

  savePublicConsent: (body: {
    formPublicKey: string;
    email: string;
    consentVersion: string;
    cookieConsent: boolean;
    privacyAcceptance: boolean;
  }) => unwrap(api.post<ApiResponse<LeadGenConsentRecord>>('/v1/lead-gen/public/consent', body)),

  listAbExperiments: () => unwrap(api.get<ApiResponse<LeadGenAbExperiment[]>>('/v1/lead-gen/ab-experiments')),

  createAbExperiment: (body: Partial<LeadGenAbExperiment>) =>
    unwrap(api.post<ApiResponse<LeadGenAbExperiment>>('/v1/lead-gen/ab-experiments', body)),

  declareAbWinner: (id: number, winnerPageId: number) =>
    unwrap(
      api.post<ApiResponse<LeadGenAbExperiment>>(`/v1/lead-gen/ab-experiments/${id}/winner`, null, {
        params: { winnerPageId },
      }),
    ),

  getEnrichment: (leadId: number) =>
    unwrap(api.get<ApiResponse<LeadGenEnrichment>>(`/v1/lead-gen/enrichment/lead/${leadId}`)),

  enrichFromEmail: (leadId: number, email: string) =>
    unwrap(
      api.post<ApiResponse<LeadGenEnrichment>>(`/v1/lead-gen/enrichment/lead/${leadId}/from-email`, null, {
        params: { email },
      }),
    ),

  upsertEnrichment: (body: Partial<LeadGenEnrichment> & { leadId: number }) =>
    unwrap(api.post<ApiResponse<LeadGenEnrichment>>('/v1/lead-gen/enrichment', body)),
};
